import type { RegistryComponentProps } from '@boost-sd/components-registry';
import { registryComponent } from '@boost-sd/components-registry/registry';
import type { DeepPartial } from '@types';
import { merge } from 'lodash-es';
import type { PropsWithChildren } from 'react';
import { createContext, useContext, useImperativeHandle, useState } from 'react';

import type { ProductItemThemeSettings } from '../types';

const ProductItemThemeSettingsContext = createContext<ProductItemThemeSettings | null>(null);

export type ProductItemThemeSettingsProviderProps = PropsWithChildren<{
  defaultSettings?: Partial<ProductItemThemeSettings>;
}>;

export const ProductItemThemeSettingsProvider = registryComponent(
  'ProductItemThemeSettingsProvider',
  ({
    children,
    defaultSettings,
    helpersRef,
  }: RegistryComponentProps<
    ProductItemThemeSettingsProviderProps,
    {
      reloadSettings: (settings?: DeepPartial<ProductItemThemeSettings>) => void;
      getSettings: () => ProductItemThemeSettings;
    }
  >) => {
    const settingsFromWindow = window?.boostSDAppConfig?.themeSettings?.productItems || {};
    const [settings, setSettings] = useState(merge({}, settingsFromWindow, defaultSettings));

    useImperativeHandle(helpersRef, () => ({
      getSettings() {
        return settings;
      },
      reloadSettings(settings) {
        const settingsFromWindow = window?.boostSDAppConfig?.themeSettings?.productItems || {};
        setSettings(merge({}, settingsFromWindow, defaultSettings, settings));
      },
    }));

    return (
      <ProductItemThemeSettingsContext.Provider value={settings}>
        {children}
      </ProductItemThemeSettingsContext.Provider>
    );
  }
);

export const useProductItemThemeSettings = () => {
  const context = useContext(ProductItemThemeSettingsContext);
  if (!context) {
    throw Error('Use useProductItemThemeSettings in ProductItemThemeSettingsProvider');
  }

  return context;
};
